import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingBag, Filter } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedSizes, setSelectedSizes] = useState({});
  const { addItem } = useCart();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch (e) {
        console.error('Failed to load products', e);
      }
    };
    load();
  }, []);

  const filtered = filter === 'all' ? products : products.filter(p => p.category === filter);
  const abbigliamento = filtered.filter(p => p.category === 'abbigliamento');
  const gadget = filtered.filter(p => p.category === 'gadget');

  const handleSizeSelect = (productId, size) => {
    setSelectedSizes(prev => ({ ...prev, [productId]: size }));
  };

  const handleAddToCart = (product) => {
    const size = product.sizes ? (selectedSizes[product.id] || product.sizes[0]) : null;
    addItem(product, 1, size);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="bg-[#FFF8F5] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF7043] mb-2">Shop Ufficiale</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-slate-900">
            Il nostro Shop
          </h1>
          <p className="text-base md:text-lg text-slate-600 mt-3 max-w-xl">
            Abbigliamento e gadget ufficiali Caribbean Blue Moon Dance School
          </p>

          {/* Filter */}
          <div className="flex items-center gap-3 mt-8">
            <Filter size={18} className="text-slate-500" />
            <div className="flex gap-2">
              {[
                { value: 'all', label: 'Tutti' },
                { value: 'abbigliamento', label: 'Abbigliamento' },
                { value: 'gadget', label: 'Gadget' }
              ].map(f => (
                <button
                  key={f.value}
                  data-testid={`filter-${f.value}`}
                  onClick={() => setFilter(f.value)}
                  className={`px-5 py-2 rounded-full text-sm font-heading font-semibold transition-all duration-200 ${
                    filter === f.value
                      ? 'bg-[#FF7043] text-white shadow-lg shadow-orange-200/50'
                      : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
        {/* Abbigliamento */}
        {(filter === 'all' || filter === 'abbigliamento') && abbigliamento.length > 0 && (
          <div className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-8">
              <span className="text-[#0288D1]">Abbigliamento</span>
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              {abbigliamento.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  selectedSize={selectedSizes[product.id]}
                  onSizeSelect={handleSizeSelect}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          </div>
        )}

        {/* Gadget */}
        {(filter === 'all' || filter === 'gadget') && gadget.length > 0 && (
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-8">
              <span className="text-[#FF7043]">Gadget</span>
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              {gadget.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  selectedSize={selectedSizes[product.id]}
                  onSizeSelect={handleSizeSelect}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductCard({ product, selectedSize, onSizeSelect, onAddToCart }) {
  return (
    <div
      data-testid={`shop-product-${product.id}`}
      className="group"
    >
      <div className="product-image-container rounded-2xl overflow-hidden bg-white aspect-square mb-4 shadow-md shadow-slate-200/30 relative">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
          <button
            data-testid={`quick-add-${product.id}`}
            onClick={() => onAddToCart(product)}
            className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 px-5 py-2.5 bg-white text-slate-900 font-heading font-bold text-sm rounded-full shadow-xl flex items-center gap-2"
          >
            <ShoppingBag size={16} /> Aggiungi
          </button>
        </div>
      </div>
      <h3 className="font-heading font-semibold text-sm md:text-base text-slate-900 mb-1">
        {product.name.replace(' Caribbean Blue Moon', '')}
      </h3>
      <p className="text-xs text-slate-500 line-clamp-1 mb-2">{product.description}</p>

      {product.sizes && product.sizes.length > 1 && (
        <div className="mb-2">
          <Select
            value={selectedSize || product.sizes[0]}
            onValueChange={(val) => onSizeSelect(product.id, val)}
          >
            <SelectTrigger className="h-8 text-xs w-full" data-testid={`size-select-${product.id}`}>
              <SelectValue placeholder="Taglia" />
            </SelectTrigger>
            <SelectContent>
              {product.sizes.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="font-heading font-bold text-lg text-[#FF7043]">€{product.price.toFixed(2)}</p>
        <button
          data-testid={`add-cart-btn-${product.id}`}
          onClick={() => onAddToCart(product)}
          className="px-4 py-2 bg-[#FF7043] text-white text-xs font-heading font-bold rounded-full btn-caribbean"
        >
          + Carrello
        </button>
      </div>
    </div>
  );
}
